import { z } from "zod"

export const sourceSchema = z.object({
  name: z.string().min(1, "Name is required"),
  slug: z
    .string()
    .min(1, "Slug is required")
    .regex(/^[a-z0-9-]+$/, "Slug must be lowercase alphanumeric with hyphens"),
  providerType: z.string().min(1, "Provider type is required"),
  verifyConfig: z.string().optional(),
  rateLimitRps: z.coerce.number().int().min(0, "Must be 0 or greater").optional(),
  spikeProtection: z.boolean().optional(),
  maxIngestRps: z.coerce.number().int().min(0, "Must be 0 or greater").optional(),
  status: z.enum(["ACTIVE", "INACTIVE"]).optional(),
  brokerType: z.enum(["none", "sqs"]).optional(),
  brokerQueueUrl: z.string().optional(),
  brokerRegion: z.string().optional(),
  brokerMaxMessages: z.coerce.number().int().min(1).max(10).optional(),
  brokerWaitTimeSeconds: z.coerce.number().int().min(0).max(20).optional(),
  responseStatusCode: z.coerce.number().int().min(100).max(599).optional(),
  responseBody: z.string().optional(),
  responseContentType: z.string().optional(),
  dedupStrategy: z.enum(["exact", "field_based", "none"]).optional(),
  dedupFields: z.string().optional(),
  dedupWindow: z.string().optional(),
})

export type SourceFormValues = z.infer<typeof sourceSchema>

export const retryPolicySchema = z.object({
  maxAttempts: z.coerce.number().int().min(1).max(100),
  initialDelay: z.string().min(1, "Required"),
  maxDelay: z.string().min(1, "Required"),
  backoffFactor: z.coerce.number().min(1).max(10),
})

const optionalRetryPolicy = z.preprocess((val) => {
  if (!val || typeof val !== "object") return undefined
  const obj = val as Record<string, unknown>
  const hasValues = obj.initialDelay || obj.maxDelay || (obj.maxAttempts !== undefined && obj.maxAttempts !== "")
  return hasValues ? val : undefined
}, retryPolicySchema.optional())

export const oauth2ConfigSchema = z.object({
  tokenUrl: z.string().url("Must be a valid URL"),
  clientId: z.string().min(1, "Client ID is required"),
  clientSecret: z.string().min(1, "Client Secret is required"),
  scopes: z.string().optional(),
  audience: z.string().optional(),
})

export type OAuth2ConfigFormValues = z.infer<typeof oauth2ConfigSchema>

export const destinationTypeValues = ["HTTP", "SQS", "EVENTBRIDGE", "PUBSUB", "KAFKA", "RABBITMQ", "CONNECTOR"] as const
export type DestinationTypeValue = (typeof destinationTypeValues)[number]

export const saslMechanismValues = ["PLAIN", "SCRAM-SHA-256", "SCRAM-SHA-512", ""] as const
export type SaslMechanismValue = (typeof saslMechanismValues)[number]

export const destinationTypeLabels: Record<DestinationTypeValue, string> = {
  HTTP: "HTTP",
  SQS: "SQS",
  EVENTBRIDGE: "EventBridge",
  PUBSUB: "Pub/Sub",
  KAFKA: "Kafka",
  RABBITMQ: "RabbitMQ",
  CONNECTOR: "Connector",
}

export const healthConfigSchema = z.object({
  windowHours: z.coerce.number().int().min(1, "Must be at least 1 hour").max(720, "Maximum 720 hours"),
  disableBelow: z.coerce.number().min(0, "Must be 0 or greater").max(1, "Must be 1 or less"),
})

export const optionalHealthConfig = z.preprocess((val) => {
  if (!val || typeof val !== "object") return undefined
  const obj = val as Record<string, unknown>
  const hasValues = (obj.windowHours !== undefined && obj.windowHours !== "") || (obj.disableBelow !== undefined && obj.disableBelow !== "")
  return hasValues ? val : undefined
}, healthConfigSchema.optional())

export const outputFormatValues = ["default", "cloudevents"] as const
export type OutputFormatValue = (typeof outputFormatValues)[number]

export const outputFormatLabels: Record<OutputFormatValue, string> = {
  default: "Default",
  cloudevents: "CloudEvents v1.0",
}

export const destinationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  type: z.enum(destinationTypeValues).optional(),
  url: z.string().url("Must be a valid URL").or(z.literal("")),
  deliveryMode: z.enum(["PUSH", "POLL"]).optional(),
  timeoutMs: z.coerce.number().int().min(100, "Minimum 100ms").max(300000, "Maximum 300000ms").optional(),
  rateLimitRps: z.coerce.number().int().min(0, "Must be 0 or greater").optional(),
  retryPolicy: optionalRetryPolicy,
  headers: z.string().optional(),
  status: z.enum(["ACTIVE", "INACTIVE"]).optional(),
  authType: z.enum(["NONE", "HEADER", "OAUTH2", "MTLS"]).optional(),
  oauth2Config: oauth2ConfigSchema.optional(),
  mtlsCert: z.string().optional(),
  mtlsKey: z.string().optional(),
  ordered: z.boolean().optional(),
  outputFormat: z.enum(outputFormatValues).optional(),
  healthConfig: optionalHealthConfig,
  sqsQueueUrl: z.string().optional(),
  sqsRegion: z.string().optional(),
  sqsAccessKeyId: z.string().optional(),
  sqsSecretAccessKey: z.string().optional(),
  ebEventBusName: z.string().optional(),
  ebSource: z.string().optional(),
  ebDetailType: z.string().optional(),
  ebRegion: z.string().optional(),
  psProjectId: z.string().optional(),
  psTopicId: z.string().optional(),
  psCredentialsJson: z.string().optional(),
  kafkaBrokers: z.string().optional(),
  kafkaTopic: z.string().optional(),
  kafkaSaslMechanism: z.enum(saslMechanismValues).optional(),
  kafkaSaslUsername: z.string().optional(),
  kafkaSaslPassword: z.string().optional(),
  rmqUrl: z.string().optional(),
  rmqExchange: z.string().optional(),
  rmqRoutingKey: z.string().optional(),
  connectorType: z.enum(["slack", "discord", "teams", "email", ""]).optional(),
  autoDeletePayload: z.boolean().optional(),
}).superRefine((data, ctx) => {
  const destType = data.type ?? "HTTP"
  if (destType === "HTTP" && data.deliveryMode !== "POLL" && !data.url) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "URL is required for HTTP push mode",
      path: ["url"],
    })
  }
  if (destType === "SQS" && !data.sqsQueueUrl) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Queue URL is required", path: ["sqsQueueUrl"] })
  }
  if (destType === "SQS" && !data.sqsRegion) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Region is required", path: ["sqsRegion"] })
  }
  if (destType === "EVENTBRIDGE" && !data.ebEventBusName) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Event bus name is required", path: ["ebEventBusName"] })
  }
  if (destType === "EVENTBRIDGE" && !data.ebRegion) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Region is required", path: ["ebRegion"] })
  }
  if (destType === "PUBSUB" && !data.psProjectId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Project ID is required", path: ["psProjectId"] })
  }
  if (destType === "PUBSUB" && !data.psTopicId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Topic ID is required", path: ["psTopicId"] })
  }
  if (destType === "KAFKA" && !data.kafkaBrokers) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Brokers are required", path: ["kafkaBrokers"] })
  }
  if (destType === "KAFKA" && !data.kafkaTopic) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Topic is required", path: ["kafkaTopic"] })
  }
  if (destType === "RABBITMQ" && !data.rmqUrl) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "URL is required", path: ["rmqUrl"] })
  }
  if (destType === "RABBITMQ" && !data.rmqExchange) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Exchange is required", path: ["rmqExchange"] })
  }
  if (destType === "CONNECTOR" && !data.connectorType) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Connector type is required", path: ["connectorType"] })
  }
  if (destType === "CONNECTOR" && (data.connectorType === "slack" || data.connectorType === "discord" || data.connectorType === "teams") && !data.url) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "URL is required for this connector type", path: ["url"] })
  }
})

export type DestinationFormValues = z.infer<typeof destinationSchema>

export const bodyMatchRuleSchema = z.object({
  path: z.string().min(1, "Path is required"),
  operator: z.enum(["eq", "neq", "contains", "exists", "not_exists"]),
  value: z.string(),
})

const filterRuleOperators = [
  "$eq", "$neq", "$gt", "$gte", "$lt", "$lte",
  "$in", "$nin", "$regex", "$exists",
] as const

export const filterRuleSchema = z.object({
  path: z.string().optional(),
  operator: z.enum(filterRuleOperators),
  value: z.string().optional(),
})

export type FilterRuleFormValue = z.infer<typeof filterRuleSchema>

export const subscriptionSchema = z.object({
  name: z.string().min(1, "Name is required"),
  sourceId: z.string().uuid("Must be a valid UUID"),
  destinationId: z.string().uuid("Must be a valid UUID"),
  eventTypes: z.string().optional(),
  regex: z.string().optional(),
  bodyMatchRules: z.array(bodyMatchRuleSchema).optional(),
  filterRules: z.array(filterRuleSchema).optional(),
  envelope: z.boolean().optional(),
  transformHeaders: z.string().optional(),
  transformationId: z.string().uuid().optional().or(z.literal("")),
  enabled: z.boolean().optional(),
  delay: z.string().regex(/^\d+[hms]$/, "Must be a duration like 30s, 5m").or(z.literal("")).optional(),
  channels: z.string().optional(),
})

export type SubscriptionFormValues = z.infer<typeof subscriptionSchema>

export const purgeDLQSchema = z.object({
  olderThan: z
    .string()
    .min(1, "Required")
    .regex(/^\d+[hms]$/, "Must be a duration like 168h, 30m, 60s"),
})

export type PurgeDLQFormValues = z.infer<typeof purgeDLQSchema>

export const apiKeySchema = z.object({
  name: z.string().min(1, "Name is required"),
  scopes: z.string().optional(),
  sourceIds: z.string().optional(),
  expiresAt: z.string().optional(),
})

export type ApiKeyFormValues = z.infer<typeof apiKeySchema>

export const applicationSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
})

export type ApplicationFormValues = z.infer<typeof applicationSchema>

export const endpointSchema = z.object({
  applicationId: z.string().uuid("Must be a valid UUID"),
  type: z.enum(destinationTypeValues).optional(),
  url: z.string().url("Must be a valid URL").or(z.literal("")),
  deliveryMode: z.enum(["PUSH", "POLL"]).optional(),
  timeoutMs: z.coerce.number().int().min(100, "Minimum 100ms").max(300000, "Maximum 300000ms").optional(),
  rateLimitRps: z.coerce.number().int().min(0, "Must be 0 or greater").optional(),
  retryPolicy: optionalRetryPolicy,
  headers: z.string().optional(),
  eventTypes: z.string().optional(),
  status: z.enum(["ACTIVE", "INACTIVE"]).optional(),
  authType: z.enum(["NONE", "HEADER", "OAUTH2", "MTLS"]).optional(),
  oauth2Config: oauth2ConfigSchema.optional(),
  mtlsCert: z.string().optional(),
  mtlsKey: z.string().optional(),
  ordered: z.boolean().optional(),
  outputFormat: z.enum(outputFormatValues).optional(),
  healthConfig: optionalHealthConfig,
  sqsQueueUrl: z.string().optional(),
  sqsRegion: z.string().optional(),
  sqsAccessKeyId: z.string().optional(),
  sqsSecretAccessKey: z.string().optional(),
  ebEventBusName: z.string().optional(),
  ebSource: z.string().optional(),
  ebDetailType: z.string().optional(),
  ebRegion: z.string().optional(),
  psProjectId: z.string().optional(),
  psTopicId: z.string().optional(),
  psCredentialsJson: z.string().optional(),
  kafkaBrokers: z.string().optional(),
  kafkaTopic: z.string().optional(),
  kafkaSaslMechanism: z.enum(saslMechanismValues).optional(),
  kafkaSaslUsername: z.string().optional(),
  kafkaSaslPassword: z.string().optional(),
  rmqUrl: z.string().optional(),
  rmqExchange: z.string().optional(),
  rmqRoutingKey: z.string().optional(),
  connectorType: z.enum(["slack", "discord", "teams", "email", ""]).optional(),
  autoDeletePayload: z.boolean().optional(),
  transformationId: z.string().uuid().optional().or(z.literal("")),
  delay: z.string().regex(/^\d+[hms]$/, "Must be a duration like 30s, 5m").or(z.literal("")).optional(),
  channels: z.string().optional(),
}).superRefine((data, ctx) => {
  const destType = data.type ?? "HTTP"
  if (destType === "HTTP" && data.deliveryMode !== "POLL" && !data.url) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "URL is required for HTTP push mode",
      path: ["url"],
    })
  }
  if (destType === "SQS" && !data.sqsQueueUrl) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Queue URL is required", path: ["sqsQueueUrl"] })
  }
  if (destType === "SQS" && !data.sqsRegion) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Region is required", path: ["sqsRegion"] })
  }
  if (destType === "EVENTBRIDGE" && !data.ebEventBusName) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Event bus name is required", path: ["ebEventBusName"] })
  }
  if (destType === "EVENTBRIDGE" && !data.ebRegion) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Region is required", path: ["ebRegion"] })
  }
  if (destType === "PUBSUB" && !data.psProjectId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Project ID is required", path: ["psProjectId"] })
  }
  if (destType === "PUBSUB" && !data.psTopicId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Topic ID is required", path: ["psTopicId"] })
  }
  if (destType === "KAFKA" && !data.kafkaBrokers) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Brokers are required", path: ["kafkaBrokers"] })
  }
  if (destType === "KAFKA" && !data.kafkaTopic) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Topic is required", path: ["kafkaTopic"] })
  }
  if (destType === "RABBITMQ" && !data.rmqUrl) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "URL is required", path: ["rmqUrl"] })
  }
  if (destType === "RABBITMQ" && !data.rmqExchange) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Exchange is required", path: ["rmqExchange"] })
  }
  if (destType === "CONNECTOR" && !data.connectorType) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Connector type is required", path: ["connectorType"] })
  }
  if (destType === "CONNECTOR" && (data.connectorType === "slack" || data.connectorType === "discord" || data.connectorType === "teams") && !data.url) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "URL is required for this connector type", path: ["url"] })
  }
})

export type EndpointFormValues = z.infer<typeof endpointSchema>

export const alertChannelValues = ["WEBHOOK", "EMAIL", "SLACK"] as const
export type AlertChannelValue = (typeof alertChannelValues)[number]

export const alertChannelLabels: Record<AlertChannelValue, string> = {
  WEBHOOK: "Webhook",
  EMAIL: "Email",
  SLACK: "Slack",
}

export const alertRuleSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  conditionType: z.enum(["dlq_threshold", "outbound_dlq_threshold", "backpressure"]),
  threshold: z.coerce.number().int().min(1, "Must be at least 1"),
  channel: z.enum(alertChannelValues).optional(),
  webhookUrl: z.string().url("Must be a valid URL").or(z.literal("")).optional(),
  emailTo: z.string().optional(),
  emailSubjectTemplate: z.string().optional(),
  slackWebhookUrl: z.string().url("Must be a valid URL").or(z.literal("")).optional(),
  slackChannel: z.string().optional(),
  cooldown: z
    .string()
    .min(1, "Required")
    .regex(/^\d+[hms]$/, "Must be a duration like 1h, 30m, 60s"),
  enabled: z.boolean().optional(),
}).superRefine((data, ctx) => {
  const channel = data.channel ?? "WEBHOOK"
  if (channel === "WEBHOOK" && !data.webhookUrl) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Webhook URL is required",
      path: ["webhookUrl"],
    })
  }
  if (channel === "EMAIL") {
    if (!data.emailTo?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "At least one recipient email is required",
        path: ["emailTo"],
      })
    }
  }
  if (channel === "SLACK") {
    if (!data.slackWebhookUrl) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Slack webhook URL is required",
        path: ["slackWebhookUrl"],
      })
    }
  }
})

export type AlertRuleFormValues = z.infer<typeof alertRuleSchema>

function optionalJSON(fieldName: string) {
  return z.string().refine(
    (val) => {
      if (!val.trim()) return true
      try { JSON.parse(val); return true } catch { return false }
    },
    { message: `${fieldName} must be valid JSON` },
  )
}

export const eventTypeSchemaFormSchema = z.object({
  eventType: z.string().min(1, "Event type is required"),
  description: z.string(),
  schema: optionalJSON("Schema"),
  example: optionalJSON("Example"),
})

export type EventTypeSchemaFormValues = z.infer<typeof eventTypeSchemaFormSchema>

export const transformationSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  description: z.string(),
  code: z.string().min(1, "Code is required"),
  failOpen: z.boolean().optional(),
  timeoutMs: z.coerce.number().int().min(50, "Minimum 50ms").max(10000, "Maximum 10000ms").optional(),
  enabled: z.boolean().optional(),
  envVars: z.string().optional(),
})

export type TransformationFormValues = z.infer<typeof transformationSchema>

export const testTransformationSchema = z.object({
  code: z.string().min(1, "Code is required"),
  payload: z.string().min(1, "Payload is required"),
  eventType: z.string().min(1, "Event type is required"),
  timeoutMs: z.number().int().min(50, "Minimum 50ms").max(10000, "Maximum 10000ms").optional(),
})

export type TestTransformationFormValues = z.infer<typeof testTransformationSchema>

export const sendMessageSchema = z.object({
  applicationId: z.string().uuid("Must be a valid UUID"),
  eventType: z.string().min(1, "Event type is required"),
  payload: z.string().min(1, "Payload is required"),
  idempotencyKey: z.string().optional(),
})

export type SendMessageFormValues = z.infer<typeof sendMessageSchema>

export const broadcastMessageSchema = z.object({
  applicationId: z.string().uuid("Must be a valid UUID"),
  eventType: z.string().min(1, "Event type is required"),
  payload: z.string().min(1, "Payload is required"),
  idempotencyKey: z.string().optional(),
})

export type BroadcastMessageFormValues = z.infer<typeof broadcastMessageSchema>

export const sendDynamicMessageSchema = z.object({
  url: z.string().url("Must be a valid URL"),
  eventType: z.string().min(1, "Event type is required"),
  payload: z.string().min(1, "Payload is required"),
  headers: z.string().optional().refine(
    (val) => {
      if (!val?.trim()) return true
      try { JSON.parse(val); return true } catch { return false }
    },
    { message: "Headers must be valid JSON" },
  ),
  signingSecret: z.string().optional(),
  timeoutMs: z.coerce.number().int().min(100, "Minimum 100ms").max(300000, "Maximum 300000ms").optional().or(z.literal("")),
  rateLimitRps: z.coerce.number().int().min(0, "Must be 0 or greater").optional().or(z.literal("")),
  maxAttempts: z.coerce.number().int().min(1, "Must be at least 1").max(100, "Maximum 100").optional().or(z.literal("")),
  initialDelay: z.string().optional(),
  maxDelay: z.string().optional(),
  backoffFactor: z.coerce.number().min(1, "Must be at least 1").max(10, "Maximum 10").optional().or(z.literal("")),
  idempotencyKey: z.string().optional(),
})

export type SendDynamicMessageFormValues = z.infer<typeof sendDynamicMessageSchema>

export const streamSchema = z.object({
  applicationId: z.string().uuid("Must be a valid UUID"),
  name: z.string().min(1, "Name is required").max(255),
  retentionDays: z.coerce.number().int().min(1, "Must be at least 1 day").max(365, "Maximum 365 days").optional(),
  status: z.enum(["ACTIVE", "PAUSED"]).optional(),
})

export type StreamFormValues = z.infer<typeof streamSchema>

export const streamConsumerSchema = z.object({
  streamId: z.string().uuid("Must be a valid UUID"),
  name: z.string().min(1, "Name is required").max(255),
})

export type StreamConsumerFormValues = z.infer<typeof streamConsumerSchema>

export const streamSinkSchema = z.object({
  streamId: z.string().uuid("Must be a valid UUID"),
  name: z.string().min(1, "Name is required").max(255),
  sinkType: z.enum(["S3", "WEBHOOK", "POSTGRES"]),
  config: z.string().min(1, "Config is required").refine(
    (val) => {
      try { JSON.parse(val); return true } catch { return false }
    },
    { message: "Config must be valid JSON" },
  ),
  batchSize: z.coerce.number().int().min(1, "Must be at least 1").max(10000, "Maximum 10000").optional(),
  flushInterval: z
    .string()
    .regex(/^\d+[hms]$/, "Must be a duration like 60s, 5m, 1h")
    .optional(),
  status: z.enum(["ACTIVE", "PAUSED"]).optional(),
})

export type StreamSinkFormValues = z.infer<typeof streamSinkSchema>
